/** @jsx jsx */
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import type { DropdownMenuItemProps } from '@radix-ui/react-dropdown-menu';
import type { ReactNode } from 'react';
import type { WithDataAttributeProp } from '@balance-web/core';
import {
  buildDataAttributes,
  getTestIdFromDataAttributes,
  jsx,
} from '@balance-web/core';

import { useTestId } from '../context';

import { useItemStyles } from './shared-styles';

export type ItemContainerProps = WithDataAttributeProp<
  {
    children: ReactNode;
    /** Optional value kept for backward-compatibility with consumers that
     *  previously relied on the (incorrect) RadioItem typing. The underlying
     *  `DropdownMenuPrimitive.Item` accepts arbitrary HTML attributes so the
     *  value is harmlessly spread through. */
    value?: string;
  } & Omit<DropdownMenuItemProps, 'onClick'>
>;

export const ItemContainer = ({ data, ...props }: ItemContainerProps) => {
  const itemStyles = useItemStyles();

  const parentTestId = useTestId();
  const dataAttributes = buildDataAttributes(data);

  const itemTestId = getTestIdFromDataAttributes(data);
  const prefixedItemTestId =
    parentTestId && itemTestId ? `${parentTestId}-${itemTestId}` : itemTestId;

  const triggerTestIdProp = prefixedItemTestId
    ? { 'data-testid': prefixedItemTestId }
    : {};

  return (
    <DropdownMenuPrimitive.Item
      css={props.asChild ? undefined : itemStyles}
      {...props}
      {...dataAttributes}
      {...triggerTestIdProp}
    />
  );
};
